import { useReducer, useEffect } from 'react'
import { useOrder } from '@/context'
import { getJadlogTracking, getTotalExpressTracking, getPatrusTracking, fetchPatrusToken, PatrusUnauthorizedError } from '@/services'
import { PATRUS_TOKEN_KEY } from '@/context'
import type { JadlogConsultaItem, TotalExpressShipment, PatrusTrackingData } from '@/interfaces'

type JadlogState = {
  tracking: JadlogConsultaItem | null
  error: string | null
  isLoading: boolean
}

type JadlogAction =
  | { type: 'fetch' }
  | { type: 'success'; item: JadlogConsultaItem | null }
  | { type: 'error'; message: string }

function jadlogReducer(_: JadlogState, action: JadlogAction): JadlogState {
  switch (action.type) {
    case 'fetch':
      return { tracking: null, error: null, isLoading: true }
    case 'success':
      return { tracking: action.item, error: null, isLoading: false }
    case 'error':
      return { tracking: null, error: action.message, isLoading: false }
  }
}

type TotalExpressState = {
  shipment: TotalExpressShipment | null
  error: string | null
  isLoading: boolean
}

type TotalExpressAction =
  | { type: 'fetch' }
  | { type: 'success'; shipment: TotalExpressShipment | null }
  | { type: 'error'; message: string }

function totalExpressReducer(_: TotalExpressState, action: TotalExpressAction): TotalExpressState {
  switch (action.type) {
    case 'fetch':
      return { shipment: null, error: null, isLoading: true }
    case 'success':
      return { shipment: action.shipment, error: null, isLoading: false }
    case 'error':
      return { shipment: null, error: action.message, isLoading: false }
  }
}

type PatrusState = {
  data: PatrusTrackingData | null
  error: string | null
  isLoading: boolean
}

type PatrusAction =
  | { type: 'fetch' }
  | { type: 'success'; data: PatrusTrackingData | null }
  | { type: 'error'; message: string }

function patrusReducer(_: PatrusState, action: PatrusAction): PatrusState {
  switch (action.type) {
    case 'fetch':
      return { data: null, error: null, isLoading: true }
    case 'success':
      return { data: action.data, error: null, isLoading: false }
    case 'error':
      return { data: null, error: action.message, isLoading: false }
  }
}

const initialJadlogState: JadlogState = { tracking: null, error: null, isLoading: false }
const initialTotalExpressState: TotalExpressState = { shipment: null, error: null, isLoading: false }
const initialPatrusState: PatrusState = { data: null, error: null, isLoading: false }

export function useRastreio() {
  const { order, interactions, isJadlogCarrier, isTotalExpressCarrier, isPatrusCarrier } = useOrder()
  const [jadlogState, jadlogDispatch] = useReducer(jadlogReducer, initialJadlogState)
  const [totalExpressState, totalExpressDispatch] = useReducer(totalExpressReducer, initialTotalExpressState)
  const [patrusState, patrusDispatch] = useReducer(patrusReducer, initialPatrusState)

  useEffect(() => {
    if (!order || !isJadlogCarrier) return

    let cancelled = false
    jadlogDispatch({ type: 'fetch' })

    const isDelivered = order.packageAttachment.packages[0]?.courierStatus?.finished === true

    getJadlogTracking(order)
      .then((response) => {
        if (cancelled) return
        const item = response.consulta[0]
        if (item?.erro) {
          jadlogDispatch({
            type: 'error',
            message: isDelivered
              ? 'Pedido já entregue, dados de rastreamento indisponíveis.'
              : 'Rastreamento não disponível para este pedido.',
          })
        } else {
          jadlogDispatch({ type: 'success', item: item ?? null })
        }
      })
      .catch(() => {
        if (cancelled) return
        jadlogDispatch({
          type: 'error',
          message: isDelivered
            ? 'Pedido já entregue, dados de rastreamento indisponíveis.'
            : 'Rastreamento não disponível no momento.',
        })
      })

    return () => { cancelled = true }
  }, [order?.orderId, isJadlogCarrier])

  useEffect(() => {
    if (!order || !isTotalExpressCarrier) return

    let cancelled = false
    totalExpressDispatch({ type: 'fetch' })

    const isDelivered = order.packageAttachment.packages[0]?.courierStatus?.finished === true

    getTotalExpressTracking(order)
      .then((response) => {
        if (cancelled) return
        const shipment = response.data[0] ?? null
        if (!shipment) {
          totalExpressDispatch({
            type: 'error',
            message: isDelivered
              ? 'Pedido já entregue, dados de rastreamento indisponíveis.'
              : 'Rastreamento não disponível para este pedido.',
          })
        } else {
          totalExpressDispatch({ type: 'success', shipment })
        }
      })
      .catch(() => {
        if (cancelled) return
        totalExpressDispatch({
          type: 'error',
          message: isDelivered
            ? 'Pedido já entregue, dados de rastreamento indisponíveis.'
            : 'Rastreamento não disponível no momento.',
        })
      })

    return () => { cancelled = true }
  }, [order?.orderId, isTotalExpressCarrier])

  useEffect(() => {
    if (!order || !isPatrusCarrier) return

    let cancelled = false
    patrusDispatch({ type: 'fetch' })

    const isDelivered = order.packageAttachment.packages[0]?.courierStatus?.finished === true

    const errorMessage = (delivered: boolean) => delivered
      ? 'Pedido já entregue, dados de rastreamento indisponíveis.'
      : 'Rastreamento não disponível no momento.'

    async function loadTracking() {
      let token = localStorage.getItem(PATRUS_TOKEN_KEY)

      if (!token) {
        token = await fetchPatrusToken()
        localStorage.setItem(PATRUS_TOKEN_KEY, token)
      }

      if (cancelled) return

      let response
      try {
        response = await getPatrusTracking(order!, token)
      } catch (err) {
        if (!(err instanceof PatrusUnauthorizedError)) throw err
        // Token expirado — busca novo e tenta de novo
        if (cancelled) return
        token = await fetchPatrusToken()
        localStorage.setItem(PATRUS_TOKEN_KEY, token)
        if (cancelled) return
        response = await getPatrusTracking(order!, token)
      }

      if (cancelled) return
      const data = response[0]?.Data ?? null
      patrusDispatch(
        data
          ? { type: 'success', data }
          : { type: 'error', message: isDelivered ? 'Pedido já entregue, dados de rastreamento indisponíveis.' : 'Rastreamento não disponível para este pedido.' }
      )
    }

    loadTracking().catch(() => {
      if (!cancelled) patrusDispatch({ type: 'error', message: errorMessage(isDelivered) })
    })

    return () => { cancelled = true }
  }, [order?.orderId, isPatrusCarrier])

  return {
    order,
    interactions,
    jadlogTracking: jadlogState.tracking,
    jadlogError: jadlogState.error,
    isLoadingJadlog: jadlogState.isLoading,
    isJadlogCarrier,
    totalExpressShipment: totalExpressState.shipment,
    totalExpressError: totalExpressState.error,
    isLoadingTotalExpress: totalExpressState.isLoading,
    isTotalExpressCarrier,
    patrusData: patrusState.data,
    patrusError: patrusState.error,
    isLoadingPatrus: patrusState.isLoading,
    isPatrusCarrier,
  }
}
